import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Spinner, Row, Col, ListGroup, Badge, Accordion, Modal } from 'react-bootstrap';
import { getSurvey, getSessions, deleteSurvey } from '../services/api';

const SurveyDetail = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [surveyData, sessionsData] = await Promise.all([
          getSurvey(surveyId),
          getSessions(surveyId)
        ]);
        
        setSurvey(surveyData);
        setSessions(sessionsData);
        setError(null);
      } catch (err) {
        console.error('Error fetching survey details:', err);
        setError('Failed to load survey details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [surveyId]);

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        {error}
      </Alert>
    );
  }

  if (!survey) {
    return (
      <Alert variant="warning">
        Survey not found. It may have been deleted or the ID is incorrect.
      </Alert>
    );
  }

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      await deleteSurvey(surveyId);
      navigate('/surveys', { replace: true });
    } catch (err) {
      console.error('Error deleting survey:', err);
      setError('Failed to delete survey. Please try again later.');
      setShowDeleteModal(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getQuestionTypeBadge = (type) => {
    const variants = {
      text: 'primary',
      select: 'success',
      checkbox: 'info',
      radio: 'warning'
    };
    
    return (
      <Badge bg={variants[type] || 'secondary'} className="me-2">
        {type}
      </Badge>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{survey.title}</h1>
        <div>
          <Button onClick={() => setShowDeleteModal(true)} variant="outline-danger" className="me-2">
            <i className="bi bi-trash"></i> Delete
          </Button>
          <Button as={Link} to={`/surveys/${surveyId}/edit`} variant="outline-secondary" className="me-2">
            <i className="bi bi-pencil"></i> Edit Survey
          </Button>
          <Button as={Link} to={`/surveys/${surveyId}/sessions/create`} variant="primary" className="me-2">
            Start Session
          </Button>
          <Button as={Link} to={`/surveys/${surveyId}/sessions`} variant="outline-primary">
            View Sessions ({sessions.length})
          </Button>
        </div>
      </div>
      
      {survey.description && (
        <Card className="mb-4">
          <Card.Body>
            <Card.Text>{survey.description}</Card.Text>
          </Card.Body>
        </Card>
      )}

      <Row className="mb-4">
        <Col md={8}>
          <h2 className="mb-3">Questions</h2>
          
          {/* Question Groups */}
          {survey.groups && survey.groups.length > 0 && (
            <Accordion defaultActiveKey="0" className="mb-4">
              {survey.groups.map((group, index) => (
                <Accordion.Item key={group.id} eventKey={String(index)}>
                  <Accordion.Header>
                    <span className="fw-bold">{group.name}</span>
                    <Badge bg="secondary" className="ms-2">
                      {group.questions.length} questions
                    </Badge>
                  </Accordion.Header>
                  <Accordion.Body>
                    <ListGroup variant="flush">
                      {group.questions.map(question => (
                        <ListGroup.Item key={question.id}>
                          <div className="mb-1">
                            {getQuestionTypeBadge(question.type)}
                            <span>{question.text}</span>
                          </div>
                          
                          {question.notes && (
                            <div className="small text-muted mb-1">
                              Note: {question.notes}
                            </div>
                          )}
                          
                          {question.options && question.options.length > 0 && (
                            <div className="small">
                              Options: {question.options.join(", ")}
                            </div>
                          )}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
          
          {/* General Questions */}
          {survey.general_questions && survey.general_questions.length > 0 && (
            <>
              <h3 className="mb-3 h5">General Questions</h3>
              <ListGroup className="mb-4">
                {survey.general_questions.map(question => (
                  <ListGroup.Item key={question.id}>
                    <div className="mb-1">
                      {getQuestionTypeBadge(question.type)}
                      <span>{question.text}</span>
                    </div>
                    
                    {question.notes && (
                      <div className="small text-muted mb-1">
                        Note: {question.notes}
                      </div>
                    )}
                    
                    {question.options && question.options.length > 0 && (
                      <div className="small">
                        Options: {question.options.join(", ")}
                      </div>
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
        </Col>
        
        <Col md={4}>
          <Card>
            <Card.Header>
              <h3 className="h5 mb-0">Recent Sessions</h3>
            </Card.Header>
            <ListGroup variant="flush">
              {sessions.length > 0 ? (
                sessions.slice(0, 5).map(session => (
                  <ListGroup.Item key={session.id} action as={Link} to={`/sessions/${session.id}`}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        {session.name || `Session ${session.id.slice(0, 8)}`}
                        <div className="small text-muted">
                          {new Date(session.created_at).toLocaleString()}
                        </div>
                      </div>
                      <i className="bi bi-arrow-right"></i>
                    </div>
                  </ListGroup.Item>
                ))
              ) : (
                <ListGroup.Item className="text-center py-4">
                  No sessions yet
                </ListGroup.Item>
              )}
            </ListGroup>
            {sessions.length > 5 && (
              <Card.Footer>
                <Button 
                  as={Link} 
                  to={`/surveys/${surveyId}/sessions`} 
                  variant="outline-primary" 
                  size="sm" 
                  className="w-100"
                >
                  View All Sessions
                </Button>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => !deleteLoading && setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Survey</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete <strong>{survey?.title}</strong>?</p>
          <p className="text-danger">This action cannot be undone. All associated sessions, answers, and attachments will be permanently deleted.</p>
          {sessions.length > 0 && (
            <Alert variant="warning">
              This survey has {sessions.length} associated session{sessions.length !== 1 ? 's' : ''}. All session data will be deleted.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete Survey'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SurveyDetail;